import React, { useEffect, useMemo, useState } from 'react';
import type { SlaEntityType, SlaPolicyResponse } from '@troia-v3/types';
import { toast } from 'sonner';

import { Button, Drawer, DrawerTabBar, Input, Select, Switch, Textarea, Tooltip } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

import { useCreateSlaPolicy, useUpdateSlaPolicy } from '../hooks';
import {
  draftFromPolicy, draftToCreateRequest, draftToUpdateRequest, rankPoliciesForDisplay,
  scopeForEntityType, validatePolicyDraft, type SlaPolicyDraft,
} from '../utils/slaPolicyHelpers';
import { SlaEscalationTab } from './SlaEscalationTab';
import { SlaScopeTab } from './SlaScopeTab';
import { SlaTargetsTab } from './SlaTargetsTab';

/**
 * Drawer master-detail de edição de política de SLA (880px).
 *
 * Réplica do `PipelinesSettingsDrawer` (Plano C, C5a) região a região: rail de
 * 280px à esquerda, detalhe com `DrawerTabBar` à direita. O rail aqui é
 * NAVEGAÇÃO — mostra a ordem de avaliação para o admin ver onde a política que
 * ele edita cai na cascata. Quem reordena é a página (C17).
 *
 * Mobile (<1024px, achado da Rodada 1 de revisão): master-detail EMPILHADO,
 * mesmo mecanismo do molde (`w-full lg:w-[280px]` + toggle de estado) — NÃO
 * o `hidden lg:flex` que a primeira rodada copiou do trecho de código sem
 * perceber que ele escondia o rail inteiro abaixo de 1024px, deixando o
 * mobile sem nenhuma forma de navegar entre políticas. Diferença do molde: lá
 * o drawer abre "sem seleção" (rail é o estado inicial natural); aqui SEMPRE
 * há uma política/rascunho ativo desde a abertura (`policy`/`null=criação`
 * vêm prontos da página), então o padrão mobile é abrir no DETALHE — replicar
 * "rail primeiro" faria o usuário reclicar algo que já escolheu na página. O
 * link "Ordem de avaliação" no topo do detalhe (mobile only) leva ao rail.
 *
 * Um rascunho, um salvar: as quatro abas editam o mesmo `SlaPolicyDraft` e o
 * rodapé grava tudo num POST/PUT.
 */
export type SlaPolicyDrawerTab = 'basic' | 'targets' | 'scope' | 'escalation';

const TAB_LABELS: Record<SlaPolicyDrawerTab, string> = {
  basic: 'Básico',
  targets: 'Alvos',
  scope: 'Escopo',
  escalation: 'Escalação',
};

export interface SlaPolicyDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** `null` = criação. */
  policy: SlaPolicyResponse | null;
  /** Lista já na ordem de avaliação — alimenta o rail. */
  policies: SlaPolicyResponse[];
  /**
   * `true` quando a listagem que alimenta `policies` está FILTRADA por um
   * termo de busca. A numeração do rail deixa de ser exibida ("—"): a busca
   * acontece no SERVIDOR, então `policies` é um subconjunto e contar a
   * sequência de entrada daria a posição DENTRO do resultado da busca, não na
   * cascata real — o mesmo tipo de mentira que `rankPoliciesForDisplay`
   * corrigiu para inativas/tipos mistos (achado da revisão final ampla, 15/08).
   */
  ranksHidden?: boolean;
  onSelectPolicy: (policy: SlaPolicyResponse) => void;
}

const ENTITY_OPTIONS: Array<{ value: SlaEntityType; label: string }> = [
  { value: 'ticket', label: 'Chamados' },
  { value: 'conversation', label: 'Conversas' },
];

export const SlaPolicyDrawer: React.FC<SlaPolicyDrawerProps> = ({
  isOpen, onClose, policy, policies, ranksHidden = false, onSelectPolicy,
}) => {
  const [activeTab, setActiveTab] = useState<SlaPolicyDrawerTab>('basic');
  const [draft, setDraft] = useState<SlaPolicyDraft>(() => draftFromPolicy(policy, 'ticket'));
  // Mobile (<1024px) só: qual "coluna" do master-detail empilhado está
  // visível. `false` = detalhe (default — ver o porquê no JSDoc do
  // componente); `true` = rail, alcançado pelo link "Ordem de avaliação".
  const [showRailMobile, setShowRailMobile] = useState(false);

  const createPolicy = useCreateSlaPolicy();
  const updatePolicy = useUpdateSlaPolicy();

  // Recarrega o rascunho ao abrir e a cada troca de política no rail. Sem
  // isso o formulário continua mostrando a política anterior — é o mesmo
  // motivo da `key` que a C10 usa no `PipelineAlertsTab`. Troca de política
  // também fecha o rail no mobile (o usuário acabou de escolher — mostra o
  // detalhe dela).
  useEffect(() => {
    if (!isOpen) return;
    setDraft(draftFromPolicy(policy, policy?.entityType ?? 'ticket'));
    setActiveTab('basic');
    setShowRailMobile(false);
  }, [isOpen, policy]);

  // Posição de avaliação por política, DENTRO do grupo (mesmo entityType, só
  // ativas) — mesmo achado da Rodada 1 que corrigiu a coluna "#" da página:
  // `index + 1` sobre `policies` (mistura tipos, inclui inativas) mentia.
  // Com busca ativa (`ranksHidden`) NINGUÉM recebe número: a lista é um
  // subconjunto vindo do servidor e qualquer contagem seria da busca, não da
  // cascata.
  const ranks = useMemo(
    () => (ranksHidden ? new Map<string, number>() : rankPoliciesForDisplay(policies)),
    [policies, ranksHidden],
  );

  /** Espelho do guard 1 do servidor (409): a padrão não pode ser desativada. */
  const isDefaultPolicy = policy?.isDefault === true;

  const error = validatePolicyDraft(draft);

  const handleSave = async () => {
    if (error) {
      toast.error(error);
      return;
    }
    try {
      if (policy) await updatePolicy.mutateAsync({ id: policy.id, data: draftToUpdateRequest(draft) });
      else await createPolicy.mutateAsync(draftToCreateRequest(draft));
      onClose();
    } catch {
      // toast tratado nos hooks (C16)
    }
  };

  const tabs: SlaPolicyDrawerTab[] = ['basic', 'targets', 'scope', 'escalation'];

  const footer = (
    <>
      <Button variant="secondary" onClick={onClose} data-testid="sla-policy-drawer-cancel">
        Cancelar
      </Button>
      <Button
        variant="primary"
        onClick={handleSave}
        disabled={!!error}
        loading={policy ? updatePolicy.isPending : createPolicy.isPending}
        data-testid="sla-policy-drawer-save"
      >
        {policy ? 'Salvar alterações' : 'Criar política'}
      </Button>
    </>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      widthClass="w-[880px]"
      icon={<Icon name="clock" className="w-5 h-5" />}
      title={policy ? 'Editar política de SLA' : 'Nova política de SLA'}
      subtitle={policy ? policy.name : 'Defina os prazos e para quem eles valem'}
      footer={footer}
    >
      <div className="flex h-full min-h-0" data-testid="sla-policy-drawer">
        {/* Rail — ordem de AVALIAÇÃO, somente navegação (ver decisão 1).
            Mobile: master-detail empilhado (`showRailMobile`) — `w-full
            lg:w-[280px]`, igual ao molde (PipelinesSettingsDrawer); acima de
            1024px sempre visível ao lado do detalhe. */}
        <div
          className={`${showRailMobile ? 'flex' : 'hidden'} lg:flex w-full lg:w-[280px] flex-shrink-0 flex-col lg:border-r min-h-0`}
          style={{ borderColor: '#e5e7eb', backgroundColor: '#fcfcfd' }}
        >
          <p className="flex-shrink-0 px-4 pt-4 pb-2 text-[11px] font-semibold uppercase tracking-wide" style={{ color: '#94a3b8' }}>
            Ordem de avaliação
            {ranksHidden && (
              <span className="block normal-case tracking-normal font-normal mt-0.5" data-testid="sla-policy-drawer-ranks-hidden">
                oculta durante a busca — a lista mostra só o resultado filtrado
              </span>
            )}
          </p>
          <div className="flex-1 overflow-y-auto px-2 pb-2 min-h-0">
            {policies.map((item) => {
              const selected = item.id === policy?.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onSelectPolicy(item)}
                  className="w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg text-left mb-0.5"
                  style={{ backgroundColor: selected ? '#f5f3ff' : 'transparent' }}
                  data-testid={`sla-policy-drawer-item-${item.id}`}
                >
                  <span className="text-xs tabular-nums flex-none" style={{ color: '#94a3b8' }}>
                    {ranks.get(item.id) ?? '—'}
                  </span>
                  <span
                    className="text-sm font-semibold truncate flex-1 min-w-0"
                    style={{ color: selected ? '#7c3aed' : '#334155' }}
                  >
                    {item.name}
                  </span>
                  {item.isDefault && <Icon name="star" size={14} style={{ color: '#f59e0b' }} />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Detalhe. Mobile: some enquanto o rail estiver visível
            (`showRailMobile`) — mesma exclusão mútua do molde. */}
        <div className={`${showRailMobile ? 'hidden' : 'flex'} lg:flex flex-1 flex-col min-w-0 min-h-0`}>
          {/* Mobile: volta pro rail (lista de políticas, colunas empilhadas). */}
          <button
            type="button"
            onClick={() => setShowRailMobile(true)}
            className="lg:hidden flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b flex-shrink-0"
            style={{ color: '#7c3aed', borderColor: '#e5e7eb' }}
            data-testid="sla-policy-drawer-show-rail"
          >
            <Icon name="chevron-left" className="w-4 h-4" />
            Ordem de avaliação
          </button>
          <DrawerTabBar
            items={tabs.map((tab) => ({ id: tab, label: TAB_LABELS[tab], testid: `sla-policy-drawer-tab-${tab}` }))}
            active={activeTab}
            onChange={(id) => setActiveTab(id as SlaPolicyDrawerTab)}
            data-testid="sla-policy-drawer-tabs"
          />
          <div className="flex-1 overflow-y-auto">
            <div className="p-6">
              {activeTab === 'basic' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
                      Nome da política
                    </label>
                    <Input
                      type="text"
                      value={draft.name}
                      onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                      placeholder="Ex: Clientes Premium, Canal WhatsApp"
                      data-testid="sla-policy-drawer-field-name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
                      Descrição (opcional)
                    </label>
                    <Textarea
                      value={draft.description}
                      onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                      rows={3}
                      resize="none"
                      placeholder="Para que serve esta política"
                      data-testid="sla-policy-drawer-field-description"
                    />
                  </div>

                  <Select
                    label="Aplica-se a"
                    description={
                      policy
                        ? 'Não é editável: trocar a entidade reabriria relógios de outra coleção.'
                        : 'Chamados usam pausa por status; conversas pausam sozinhas, pela direção da última mensagem.'
                    }
                    value={draft.entityType}
                    onChange={(value) => {
                      const entityType = value as SlaEntityType;
                      setDraft({
                        ...draft,
                        entityType,
                        // Trocar para conversa zera as pausas — é o que o Zod da
                        // D1 exige (422 com pausesOn não-vazio).
                        targets: draft.targets.map((row) => (entityType === 'conversation' ? { ...row, pausesOn: [] } : row)),
                        // ...e limpa os predicados de escopo que a aba Escopo
                        // ESCONDE nessa entidade (`pipelineIds`, e o `stageIds`
                        // que nunca teve UI). Deixá-los no rascunho salvaria uma
                        // política de conversa que o motor nunca casa —
                        // `matchesScope` reprova predicado preenchido contra
                        // subject sem o campo — e invisível, porque o campo
                        // some da tela junto (ver `scopeForEntityType`).
                        scope: scopeForEntityType(draft.scope, entityType),
                      });
                    }}
                    options={ENTITY_OPTIONS}
                    disabled={!!policy}
                    data-testid="sla-policy-drawer-field-entity"
                  />

                  {/* Espelho do guard 1 do `SlaPoliciesService.validateUpdate`
                      (409 ConflictError): a política PADRÃO não pode ser
                      desativada — ela é o piso da cascata, e sem ela um item
                      que não casa com nenhuma política fica sem SLA nenhum.
                      Desabilitar aqui é o mesmo "perguntar antes de agir" do
                      `isDefaultLast` no arrasto: não mandar ao servidor o que
                      já se sabe recusado. */}
                  {(() => {
                    const statusField = (
                      <label className="flex items-center gap-2 text-sm w-fit" style={{ color: '#334155' }}>
                        <Switch
                          checked={draft.status === 'active'}
                          disabled={isDefaultPolicy}
                          onChange={(checked) => setDraft({ ...draft, status: checked ? 'active' : 'inactive' })}
                          data-testid="sla-policy-drawer-field-status"
                        />
                        Política ativa
                      </label>
                    );
                    // Tooltip SÓ quando há restrição a explicar — em política
                    // comum o campo é auto-explicativo e um tooltip a cada
                    // hover seria ruído.
                    return isDefaultPolicy ? (
                      <Tooltip content="A política padrão não pode ser desativada — ela é o piso da cascata.">
                        {statusField}
                      </Tooltip>
                    ) : statusField;
                  })()}

                  <label className="flex items-center gap-2 text-sm" style={{ color: '#334155' }}>
                    <Switch
                      checked={draft.countAiAsFirstResponse}
                      onChange={(checked) => setDraft({ ...draft, countAiAsFirstResponse: checked })}
                      data-testid="sla-policy-drawer-field-count-ai"
                    />
                    Resposta do agente de IA conta como primeira resposta
                  </label>
                  <p className="text-xs -mt-2" style={{ color: '#64748b' }}>
                    Desligar mede só atendimento humano — e a série histórica da Taxa de Resposta
                    no SLA cai de uma vez, porque hoje a resposta da IA já conta.
                  </p>
                </div>
              )}

              {activeTab === 'targets' && (
                <SlaTargetsTab draft={draft} onChange={setDraft} resetKey={policy?.id ?? 'new'} />
              )}

              {activeTab === 'scope' && (
                <SlaScopeTab draft={draft} onChange={setDraft} enabled={isOpen && activeTab === 'scope'} />
              )}

              {activeTab === 'escalation' && (
                <SlaEscalationTab draft={draft} onChange={setDraft} enabled={isOpen && activeTab === 'escalation'} />
              )}
            </div>
          </div>

          {error && (
            <p className="flex-none px-6 pb-3 text-xs" style={{ color: '#dc2626' }} data-testid="sla-policy-drawer-error">
              {error}
            </p>
          )}
        </div>
      </div>
    </Drawer>
  );
};

SlaPolicyDrawer.displayName = 'SlaPolicyDrawer';
